import { ApolloError } from "@apollo/client"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react"
import { flattenEdges } from "../../utils/apollo"
import {
  ChangeSet,
  FromCohort,
  Path,
  PathItem,
  SelectedSourceIDs,
  SyncDirection,
  SyncPlan,
  SyncPlanContext,
  ToCohort
} from "./types"

export type PathPublishContextType = {
  fromCohort: FromCohort
  toCohort?: ToCohort
  fromPath?: Path | null
  syncPlan?: SyncPlan | null
  syncPlanContext: SyncPlanContext | null
  items?: PathItem[]
  isPublishing: boolean
  onPublish: () => void
  cohortName?: string
  changes: ChangeSet
  syncDir: SyncDirection
  onClose?: () => void
  onCreateCohort?: () => void
  onSetSyncDir?: (dir: SyncDirection) => void
  error?: ApolloError
  loading?: boolean
  selectedItemSourceIds?: SelectedSourceIDs
  toggleSelectedItemSourceId?: (itemSourceId: number) => void
  setSelectedItemSourceIds?: React.Dispatch<
    React.SetStateAction<SelectedSourceIDs>
  >
  onClickStep?: (stepId: number) => void
  isSourcePath?: boolean
  // Should Publish/Sync Tips be expanded by default? (example: More about Publishing, etc)
  shouldExpandTips?: boolean
}

const defaultChangeSet = {
  syncable: [],
  ignored: [],
  stepsNoContent: [],
  stepsDraftBlocks: [],
  stepsUnpublishedBlocks: [],
  lessonsNoSteps: [],
  lessonsNoValidSteps: []
}

const PathPublishContext = createContext<PathPublishContextType>(
  {} as PathPublishContextType
)

export const useSyncCard = () => useContext(PathPublishContext)

export type PathPublishProviderProps = {
  syncPlanContext: SyncPlanContext | null
  fromCohort?: FromCohort | null
  toCohort?: ToCohort | null
  children: ReactNode
  onPublish: (syncPlan: SyncPlan) => Promise<void>
  onClose?: () => void
  loading?: boolean
  error?: ApolloError
  syncDir?: SyncDirection
  onSetSyncDir?: (dir: SyncDirection) => void
  fromPath?: Path | null
  selectedItemSourceIds?: SelectedSourceIDs
  toggleSelectedItemSourceId?: (itemSourceId: number) => void
  setSelectedItemSourceIds?: React.Dispatch<
    React.SetStateAction<SelectedSourceIDs>
  >
  onCreateCohort?: () => void
  onClickStep?: (stepId: number) => void
  shouldExpandTips?: boolean
}

const SyncCardProvider = (props: PathPublishProviderProps) => {
  const {
    error,
    onClose,
    fromCohort,
    toCohort,
    syncPlanContext,
    syncDir = "forward",
    onSetSyncDir,
    onCreateCohort,
    onPublish,
    fromPath,
    selectedItemSourceIds,
    toggleSelectedItemSourceId,
    setSelectedItemSourceIds,
    loading,
    onClickStep,
    shouldExpandTips,
    children
  } = props

  const { tc } = useTranslate()

  const isSourcePath = !!fromCohort?.is_master

  const { [syncDir]: syncPlan } = syncPlanContext as SyncPlanContext

  const [isPublishing, setIsPublishing] = useState(false)

  const items = useMemo(
    () => flattenEdges(fromPath?.items),
    [fromPath, flattenEdges]
  )

  const [changes, setChanges] = useState<ChangeSet>(defaultChangeSet)

  useEffect(() => {
    // If the sync plan is not loading, set the changes
    // This is to prevent back sync changes from being briefly reset to none
    //  when the sync plan is recalculating based on selected items
    if (items && syncPlan && !syncPlan.query?.loading) {
      setChanges(syncPlan.groupedChanges)
    }
  }, [syncPlan, items])

  const isLoading = useMemo(
    () =>
      loading ||
      !fromCohort ||
      !syncPlan ||
      !changes ||
      syncPlan.query?.loading,
    [loading, fromCohort, syncPlan, changes, syncPlan?.query?.loading]
  )

  const handlePublish = useCallback(async () => {
    if (!syncPlan) return

    setIsPublishing(true)

    await onPublish(syncPlan)
    setIsPublishing(false)
  }, [syncPlan, onPublish])

  const cohortName = useMemo(() => {
    let name = fromCohort?.name

    if (!name && isSourcePath) {
      name = fromCohort.resource?.name || tc("Source")
    }

    return name
  }, [fromCohort?.name, fromCohort?.resource])

  return fromCohort ? (
    <PathPublishContext.Provider
      value={{
        fromCohort,
        toCohort,
        fromPath,
        syncPlan,
        syncPlanContext,
        onClose,
        onCreateCohort,
        loading: isLoading,
        error,
        isPublishing,
        onPublish: handlePublish,
        items,
        changes,
        selectedItemSourceIds,
        toggleSelectedItemSourceId,
        setSelectedItemSourceIds,
        cohortName,
        syncDir,
        onSetSyncDir,
        onClickStep,
        isSourcePath,
        shouldExpandTips
      }}
    >
      {children}
    </PathPublishContext.Provider>
  ) : null
}

export default SyncCardProvider
